<script setup>
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { Link } from '@inertiajs/vue3'
import CommentLike from './CommentLike.vue'
import axios from 'axios'
import { ref, reactive, nextTick } from 'vue'
import { useTextareaAutosize } from '@vueuse/core'
import EditSvg from '@svg/new-edit.svg'
import CloseSvg from "@svg/new-close.svg";
import MenuSvg from '@svg/new-menu.svg'
import ReplySvg from '@svg/new-reply.svg'
import CommentSvg from '@svg/new-comment.svg'
import useCloudflareImage from '../../../Composables/useCloudflareImage.js'
import LinkSvg from '@svg/new-link.svg'
import CommentReplyForm from './CommentReplyForm.vue'
import VueScrollTo from 'vue-scrollto'

const { cloudflareImage } = useCloudflareImage()

const props = defineProps({
    comment: Object,
    showReplyButton: {
        type: Boolean,
        default: true
    },
    showReplyToThreadButton: {
        type: Boolean,
        default: false
    },
    showReplies: {
        type: Boolean,
        default: true
    }
})

const { textarea, input } = useTextareaAutosize()

const emit = defineEmits()

const replying = ref(false)
const editing = ref(false)

const editForm = reactive({
    body: props.comment.body_text
})

const edit = () => {
    axios.patch(`/comments/${props.comment.id}`, editForm).then((response) => {
        editing.value = false
        props.comment.body = response.data.body
    })
}

const deleteComment = (comment) => {
    if (!window.confirm('Are you sure?')) {
        return
    }

    axios.delete(`/comments/${props.comment.id}`).then(() => {
        emit('comment-deleted', comment.id)
    })
}

const subscribe = () => {
    axios.post(`/comments/${props.comment.id}/subscriptions`).then(() => {
        props.comment.user_flags.subscribed = true
    })
}

const unsubscribe = () => {
    axios.delete(`/comments/${props.comment.id}/subscriptions`).then(() => {
        props.comment.user_flags.subscribed = false
    })
}
</script>

<template>
    <div dusk="comment">
        <div class="bg-[rgba(27,32,74,1)] p-7 rounded-2xl group" :id="`comment-${comment.id}`">
            <template v-if="editing">
                <form v-on:submit.prevent="edit" class="flex items-start">
                    <textarea dusk="comment-edit-textarea" ref="textarea" class="border-0 bg-transparent w-full resize-none placeholder:text-[rgba(148,162,209,1)] text-[rgba(148,162,209,1)] text-sm focus:ring-0 p-0 leading-normal mt-2" v-model="input" required v-model.body="editForm.body"></textarea>
                    <div class="flex items-center space-x-3">
                        <button dusk="comment-edit-button" type="submit" class="px-3 h-10 max-h-10 -mr-0.5 rounded-lg leading-none bg-[rgba(107,96,255,1)] font-medium inline-flex items-center text-sm">
                            <EditSvg class="size-5 text-[rgba(233,232,255,1)]" />
                        </button>
                        <button type="button" class="px-3 h-10 max-h-10 -mr-0.5 rounded-lg leading-none bg-[rgba(35,41,89,1)] text-[rgba(112,120,176,1)] font-medium inline-flex items-center text-sm" v-on:click="editing = false">
                            <CloseSvg class="size-5" />
                        </button>
                    </div>
                </form>
            </template>
            <template v-else>
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3.5 mr-6">
                        <div class="relative shrink-0">
                            <img
                                :src="cloudflareImage(comment.user.avatar)"
                                loading="lazy"
                                class="size-10 rounded-full ring-2"
                                :class="{
                                    'ring-[rgba(65,251,220,1)]': comment.user.has_membership,
                                    'ring-transparent': !comment.user.has_membership
                                }"
                                :alt="comment.user.username"
                            >
                            <span v-if="comment.user.has_membership" class="text-[9px] bg-[rgba(65,251,220,1)] absolute -bottom-1 left-1/2 -translate-x-1/2 text-[rgba(20,22,57,1)] rounded font-medium leading-none py-[1px] px-[3px]">
                                Member
                            </span>
                        </div>
                        <div class="flex items-center flex-wrap">
                            <div class="text-[rgba(233,232,255,1)] text-sm font-semibold mr-2">{{ comment.user.username }}</div>
                            <div v-if="false" class="text-[0.65rem] border border-[rgba(1,221,189,1)] text-[rgba(1,221,189,1)] rounded-full px-2 py-1 font-medium leading-none ml-1 mr-2 mt-2">
                                Moderator
                            </div>
                            <span class="text-[rgba(112,120,176,1)] mr-2 hidden md:block">&bull;</span>
                            <time class="text-xs text-[rgba(148,162,209,1)] whitespace-nowrap mr-2" :datetime="comment.created_at.datetime" :title="comment.created_at.datetime">
                                {{ comment.created_at.human }}
                            </time>
                            <div class="mr-2 mt-2 hidden group-hover:block">
                                <Link :href="comment.permalink" target="_blank" class="inline-block p-1" title="Permalink">
                                    <LinkSvg class="size-4 text-[rgba(148,162,209,1)]" />
                                </Link>
                            </div>
                        </div>
                    </div>
                    <Menu dusk="comment-menu" as="div" class="relative" v-if="($page.props.auth.user && comment.is_parent) || (!comment.is_parent && (comment.user_can.edit || comment.user_can.delete))">
                        <MenuButton class="size-6 translate-x-2 flex justify-center items-center" aria-label="Menu">
                            <MenuSvg class="size-4 fill-[rgba(112,120,176,1)]" />
                        </MenuButton>
                        <transition
                            enter-active-class="transition ease-out"
                            enter-from-class="transform opacity-0 translate-x-2"
                            leave-active-class="transition ease-out"
                            leave-to-class="transform opacity-0 translate-x-2"
                        >
                            <MenuItems class="absolute right-5 top-0 bg-[rgba(20,22,57,1)] rounded-2xl w-full min-w-44 whitespace-nowrap -space-y-2.5 overflow-hidden ui-focus-visible:ring-2 ui-focus-visible:ring-offset-2 focus:outline-none">
                                <MenuItem dusk="comment-menu-edit" v-if="comment.user_can.edit" v-on:click="editing = !editing" as="div" class="text-sm text-[rgba(132,156,241,1)] px-5 py-4 cursor-pointer font-medium">
                                    Edit
                                </MenuItem>
                                <MenuItem dusk="comment-menu-delete" v-if="comment.user_can.delete" v-on:click="deleteComment(comment)" as="div" class="text-sm text-[rgba(132,156,241,1)] px-5 py-4 cursor-pointer font-medium">
                                    Delete
                                </MenuItem>
                                <template v-if="comment.is_parent">
                                    <MenuItem dusk="comment-menu-unsubscribe" v-if="comment.user_flags.subscribed" v-on:click="unsubscribe(comment)" as="div" class="text-sm text-[rgba(132,156,241,1)] px-5 py-4 cursor-pointer font-medium">
                                        Unsubscribe
                                    </MenuItem>
                                    <MenuItem dusk="comment-menu-subscribe" v-if="!comment.user_flags.subscribed" v-on:click="subscribe(comment)" as="div" class="text-sm text-[rgba(132,156,241,1)] px-5 py-4 cursor-pointer font-medium">
                                        Subscribe
                                    </MenuItem>
                                </template>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
                <div class="mt-5 min-w-full discussion-post break-words" v-html="comment.body"></div>
                <div class="mt-3">
                    <div class="flex flex-wrap sm:flex-nowrap items-center justify-between">
                        <div class="flex items-center space-x-6">
                            <CommentLike :comment="comment" />

                            <div class="flex items-center space-x-1 text-[rgba(112,120,176,1)]" v-if="showReplies">
                                <CommentSvg class="size-5" />
                                <span class="font-medium">{{ comment.children.length }}</span>
                            </div>
                        </div>
                        <div class="flex items-center">
                            <component v-if="showReplyToThreadButton" :is="$page.props.auth.user ? `button` : Link" :data="{ from: `${$page.url}` }" v-on:click="$page.props.auth.user ? emit('replying', true) : null" :href="`/auth/signin`" preserve-scroll preserve-state class="px-3.5 h-10 rounded-lg leading-none bg-[rgba(35,41,89,1)] text-[rgba(148,162,209,1)] font-medium inline-flex items-center space-x-1.5 text-sm">
                                <ReplySvg class="size-5" />
                                <span class="text-xs">Reply to thread</span>
                            </component>

                            <component dusk="comment-reply-toggle" v-if="showReplyButton" :is="$page.props.auth.user ? `button` : Link" :data="{ from: `${$page.url}` }" v-on:click="$page.props.auth.user ? replying = !replying : null" :href="`/auth/signin`" preserve-scroll preserve-state class="px-3.5 h-10 rounded-lg leading-none bg-[rgba(35,41,89,1)] text-[rgba(148,162,209,1)] font-medium inline-flex items-center space-x-1.5 text-sm">
                                <ReplySvg class="size-5" />
                                <span class="text-xs">Reply</span>
                            </component>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <!-- Child comments -->
        <div class="pl-3 md:pl-6 lg:pl-14 relative lg:before:absolute before:left-[5px] lg:before:left-[20px] before:w-[2px] before:top-0 before:bg-[rgba(26,28,70,1)] before:h-full [&:nth-child(n+3)]:before:-top-[30px] [&:nth-child(n+3)]:before:scale-y-125 space-y-3 lg:space-y-6">
            <div dusk="comment-reply" v-for="(child, index) in comment.children" :key="child.id" class="mt-3 lg:mt-6 bg-[rgba(27,32,74,1)] rounded-2xl relative lg:before:absolute before:h-[2px] before:w-[10px] lg:before:w-[20px] before:bg-[rgba(26,28,70,1)] before:top-[45px] before:-left-[26px] lg:before:-left-[35px] lg:after:absolute after:w-[8px] after:h-[8px] after:bg-[rgba(26,28,70,1)] after:-left-[18px] lg:after:-left-[20px] after:top-[42px] after:rotate-45">
                <Comment
                    :comment="child"
                    :show-reply-button="false"
                    :show-reply-to-thread-button="index === comment.children.length - 1"
                    :show-replies="false"
                    v-on:comment-deleted="emit('comment-deleted', $event)"
                    v-on:replying="replying = !replying"
                />
            </div>

            <CommentReplyForm
                :id="`comment_${comment.id}_reply`"
                :comment="comment"
                v-if="replying"
                v-on:replying="replying = $event"
            />
        </div>
    </div>
</template>
